import { useCallback, useState } from 'react';

import {
  fetchRobloxTopChartsMetrics,
  parseRobloxTopChartsResponse,
} from '@intelligence/v3/services/robloxTopCharts';
import { buildRobloxTopChartsChartFetchParams } from '../utils/buildRobloxTopChartsChartFetchParams';

import type { IMainGenre } from '@intelligence/types/common';
import type { IRobloxTopChartsFilterValues, IRobloxTopChartsRow } from '../types';

interface IFetchRobloxTopChartsChartOptions {
  gameIds: string[];
  sortKey?: string;
  sortOrder?: 'asc' | 'desc';
  chartMetric?: string;
}

export default function useRobloxTopChartsChart(
  robloxGenreOptionsRef?: React.MutableRefObject<IMainGenre[]>,
) {
  const [loading, setLoading] = useState(false);
  const [chartList, setChartList] = useState<IRobloxTopChartsRow[]>([]);

  const fetchChart = useCallback(async (
    filterValues: IRobloxTopChartsFilterValues,
    options: IFetchRobloxTopChartsChartOptions,
  ) => {
    const gameIds = options.gameIds.filter(Boolean);

    if (!gameIds.length) {
      setChartList([]);
      setLoading(false);
      return;
    }

    setChartList([]);
    setLoading(true);

    try {
      const params = buildRobloxTopChartsChartFetchParams(filterValues, {
        gameIds,
        sortKey: options.sortKey,
        sortOrder: options.sortOrder,
        chartMetric: options.chartMetric,
        robloxGenreOptions: robloxGenreOptionsRef?.current,
      });
      const response = await fetchRobloxTopChartsMetrics(params);
      const { list } = parseRobloxTopChartsResponse(response);
      setChartList(list);
    } catch (error) {
      console.debug('[RobloxTopCharts] fetch chart failed', error);
      setChartList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const clearChart = useCallback(() => {
    setChartList([]);
    setLoading(false);
  }, []);

  const setChartLoading = useCallback((nextLoading: boolean) => {
    setLoading(nextLoading);
  }, []);

  return {
    loading,
    chartList,
    fetchChart,
    clearChart,
    setChartLoading,
  };
}
